import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { InputAdornment } from "@material-ui/core";
import { bright_90, increaseBrightness } from "common/helpers/adjustColor";
import ValidationHelper from "common/helpers/validation.helper";
import upsdkService from "common/services/upsdkService";
import upsdkSelector from "common/store/selectors/upsdkSelector";

import PhoneIcon from "../../../assets/icons/phone.icon";
import AppButton from "../../../common/button/index.component";
import InputField from "../../../common/fields/input-field/index.component";
import ServerError from "../../../common/server-error/index.component";
import Typography from "../../../common/typography/index.component";
import CountryHelper from "../../../helpers/countryCode.helper";
import { useTranslation } from "react-i18next";
import MiscHelper from "common/helpers/misc.helper";
import {
  FORMAT_TYPE_SVG,
  FORMAT_TYPE_UNICODE,
} from "../../../constants/enums.constants";

import "./index.component.scss";

export default function PhoneVerification({
  upsdk,
  authResponse,
  primaryTextColor,
  secondaryTextColor,
  primaryColor,
  phoneVerifyContinueCallback,
  setAuthResponse,
  handleSetView,
}) {
  const [phoneVerificationError, setPhoneVerificationError] =
    useState(undefined);
  const { register, handleSubmit, errors } = useForm({ mode: "onBlur" });
  const [loading, setLoading] = useState(false);

  // variables
  const isdCode = upsdkSelector.getISDCode({ upsdk });
  const currency = upsdkSelector.getCurrency({ upsdk });
  const { t } = useTranslation();

  const onSubmit = (data) => {
    const { PhoneNumber } = data;
    const { token, name, email } = authResponse;
    setLoading(true);
    upsdkService
      .checkPhone({
        name: name,
        email: email,
        phone: `${isdCode}${PhoneNumber}`,
        provider: authResponse.provider,
        accessToken: token,
      })
      .then((response) => {
        setLoading(false);
        if (!response.success) {
          if (response.message === "new_registration_required") {
            const referralData = JSON.parse(
              localStorage.getItem("branch_ref_data"),
            );

            let referralParams = {};

            if (referralData) {
              if (referralData["~referring_link"]) {
                referralParams.code_link = referralData["~referring_link"];
              }

              if (referralData.card) {
                referralParams.referrer_card = referralData.card;
              }

              if (referralData["~channel"]) {
                referralParams.channel = referralData["~channel"];
              }

              if (referralData.link_share_time) {
                referralParams.shared_on = referralData.link_share_time;
              }

              if (referralData.share_platform) {
                referralParams.platform = referralData.share_platform;
              }
            }

            let fullNameSplit = name.split(" ");
            const [first_name, ...rest] = fullNameSplit;
            const last_name = rest.join(" ");

            upsdkService
              .signup({
                isd_code: isdCode,
                first_name,
                last_name,
                phone: PhoneNumber,
                email: email,
                referralData: referralParams,
              })
              .then((response) => {
                setPhoneVerificationError(null);
                handleSetView("otpVerify");
                setAuthResponse({
                  isd_code: isdCode,
                  customer_phone: PhoneNumber,
                  ...response,
                });
              })
              .catch((error) => {
                setPhoneVerificationError(error.message);
              })
              .finally(() => {
                setLoading(false);
              });
            return;
          } else if (response.message === "otp_sent") {
            phoneVerifyContinueCallback({
              ...authResponse,
              customer_phone: `${isdCode}${PhoneNumber}`,
            });
            return;
          } else {
            setPhoneVerificationError(response.error_message);
            return;
          }
        }
        setPhoneVerificationError(undefined);
        phoneVerifyContinueCallback({
          ...authResponse,
          customer_phone: `${isdCode}${PhoneNumber}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="phone-verification-form">
      <div className="dialog-header">
        <Typography
          variant="h2"
          weight="regular"
          className="form-sub-heading"
          fontColor={secondaryTextColor}
        >
          {t("authDialog.verifyPhoneSubHeading")}
        </Typography>
      </div>
      <div className="dialog-content">
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputField
            type="number"
            refForm={register(ValidationHelper.phoneNumber(t, isdCode))}
            name="PhoneNumber"
            errors={errors}
            label={
              <Typography
                variant="h4"
                weight="bold"
                fontColor={primaryTextColor}
                isRequired={true}
              >
                {t("authDialog.phoneNumber")}
              </Typography>
            }
            helperText={errors.PhoneNumber && errors.PhoneNumber.message}
            inputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {MiscHelper.isWindowsOS() ? (
                    <img
                      height="16px"
                      width="16px"
                      src={CountryHelper.getCountryFlag(
                        isdCode,
                        currency,
                        FORMAT_TYPE_SVG,
                      )}
                      alt="country-flag"
                    />
                  ) : (
                    CountryHelper.getCountryFlag(
                      isdCode,
                      currency,
                      FORMAT_TYPE_UNICODE,
                    )
                  )}
                  &nbsp;
                  {isdCode}
                </InputAdornment>
              ),
            }}
            inputIcon={<PhoneIcon className="field-icon" />}
            primaryTextColor={primaryTextColor}
            iconBackgroundColor={increaseBrightness(primaryColor, bright_90)}
          />
        </form>
      </div>
      {phoneVerificationError && (
        <ServerError message={phoneVerificationError} />
      )}
      <div className="dialog-footer">
        <AppButton
          fullWidth
          variant="contained"
          className="continue-button"
          isLoading={loading}
          buttonColor={primaryColor}
          onClickCallback={handleSubmit(onSubmit)}
        >
          <Typography
            variant="h3"
            weight="bold"
            className="continue-button-text"
          >
            {t("authDialog.continue")}
          </Typography>
        </AppButton>
      </div>
    </div>
  );
}
